import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, onAccent } from './designTokens';
import { DAY_NAMES_SHORT, generateSlots, getDateStr } from './clientUtils';
import { initials } from './BookingDayBand';

// T-12: terapeutens Hem-vy (Dashboard) enligt artboarden "App: Dashboard (Hem) – förslag".
// Mörkt sidhuvud med accentglöd, fyra nyckeltal som överlappar bandet, Nästa kund,
// Idag-lista med kalenderns statusar, Att hantera (inkorg), Kommande 7 dagar, Aktivitet.

const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const MSG_COLOR = '#7C3AED'; // meddelande-prick (samma lila som tidigare aktivitetslistan)

function isoWeek(d) {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - dow);
  const y0 = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x - y0) / 864e5 + 1) / 7);
}

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function hm(t) { return (t || '').slice(0, 5); }
function minutes(t) { const [h, m] = hm(t || '00:00').split(':').map(Number); return h * 60 + m; }

function fmtRelative(iso, now) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.round((now - d) / 60000);
  const time = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  const dStr = getDateStr(d);
  const todayStr = getDateStr(now);
  const yesterdayStr = getDateStr(addDays(now, -1));
  if (diffMin >= 0 && diffMin < 60) return diffMin <= 1 ? 'Nyss' : `För ${diffMin} min sedan`;
  if (diffMin >= 0 && diffMin < 6 * 60) { const h = Math.floor(diffMin / 60); return `För ${h} ${h === 1 ? 'timme' : 'timmar'} sedan`; }
  if (dStr === todayStr) return `Idag ${time}`;
  if (dStr === yesterdayStr) return `Igår ${time}`;
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${time}`;
}

function fmtDateShort(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${DAY_NAMES_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function fmtUntil(mins) {
  if (mins < 60) return `om ${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m > 0 ? `om ${h} t ${m} min` : `om ${h} t`;
}

// ─── Byggstenar ──────────────────────────────────────────────────────────────

function Card({ theme, children, style }) {
  return (
    <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden', ...style }}>
      {children}
    </div>
  );
}

function CardHeader({ theme, title, right, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink }}>{title}</h2>
        {children}
      </div>
      {right}
    </div>
  );
}

function LinkButton({ theme, onClick, children, muted }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: 'transparent', border: 'none', color: muted ? NEUTRAL.muted : theme.accent, fontSize: '12px', fontWeight: muted ? '400' : '600', cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
    >
      {children}
    </button>
  );
}

function Label({ children, color }) {
  return <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: color || NEUTRAL.muted }}>{children}</div>;
}

function Dot({ color, size = 8, style }) {
  return <span style={{ width: size, height: size, borderRadius: '999px', background: color, flexShrink: 0, display: 'inline-block', ...style }} />;
}

function EmptyNote({ children }) {
  return (
    <div style={{ margin: '12px', padding: '20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${NEUTRAL.line}`, fontSize: '12px', color: NEUTRAL.muted }}>
      {children}
    </div>
  );
}

// ─── Vyn ─────────────────────────────────────────────────────────────────────

function HomeView({ therapist, clients, theme, onOpenClient, onOpenBookings, onAddClient, onNewBooking, onCountsChanged }) {
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const [schedules, setSchedules] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);         // idag … +7 dagar, ej avbokade
  const [nextLater, setNextLater] = useState(null);     // första bokning efter 7-dagarsfönstret
  const [unseenNew, setUnseenNew] = useState([]);       // nya kundbokningar terapeuten inte sett
  const [unseenCancelled, setUnseenCancelled] = useState([]);
  const [unreadByClient, setUnreadByClient] = useState([]); // [{clientId, count, preview, latestAt}]
  const [activity, setActivity] = useState([]);
  const [nextInfo, setNextInfo] = useState({ anamnesis: null, note: null, noteAt: null, visits: null });
  const [markingAll, setMarkingAll] = useState(false);

  const clientById = (id) => clients.find(c => c.id === id) || null;
  const clientName = (id) => clientById(id)?.name || '–';

  const fetchHome = useCallback(async () => {
    const nowD = new Date();
    setNow(nowD);
    const todayStr = getDateStr(nowD);
    const endStr = getDateStr(addDays(nowD, 7));
    const cutoff = new Date(nowD.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const clientIds = clients.map(c => c.id);

    const [
      { data: schedData },
      { data: blockedData },
      { data: servicesData },
      { data: bookData },
      { data: laterData },
      { data: unseenData },
      { data: cancelledData },
      { data: unreadData },
    ] = await Promise.all([
      supabase.from('availability_schedules').select('*').eq('therapist_id', therapist.id).eq('is_active', true),
      supabase.from('blocked_times').select('*').eq('therapist_id', therapist.id).gte('date', todayStr).lte('date', endStr),
      supabase.from('services').select('id, duration, is_active').eq('therapist_id', therapist.id).eq('is_deleted', false),
      supabase.from('bookings')
        .select('id, date, start_time, end_time, client_id, service_title_snapshot, price_snapshot, service:services(title), clients(name)')
        .eq('therapist_id', therapist.id).neq('status', 'cancelled')
        .gte('date', todayStr).lte('date', endStr)
        .order('date', { ascending: true }).order('start_time', { ascending: true }),
      supabase.from('bookings')
        .select('id, date, start_time, end_time, client_id, service_title_snapshot, price_snapshot, service:services(title), clients(name)')
        .eq('therapist_id', therapist.id).neq('status', 'cancelled')
        .gt('date', endStr)
        .order('date', { ascending: true }).order('start_time', { ascending: true }).limit(1),
      supabase.from('bookings')
        .select('id, date, start_time, client_id, created_at, service_title_snapshot, service:services(title), clients(name)')
        .eq('therapist_id', therapist.id).eq('therapist_seen', false).neq('status', 'cancelled')
        .order('created_at', { ascending: false }).limit(10),
      supabase.from('bookings')
        .select('id, date, start_time, client_id, created_at, clients(name)')
        .eq('therapist_id', therapist.id).eq('status', 'cancelled').eq('booked_by', 'client').eq('therapist_seen', false)
        .order('date', { ascending: false }).limit(10),
      supabase.from('messages')
        .select('client_id, content, created_at')
        .eq('therapist_id', therapist.id).eq('sender_role', 'client').eq('read', false)
        .order('created_at', { ascending: false }).limit(50),
    ]);

    setSchedules(schedData || []);
    setBlockedTimes(blockedData || []);
    setServices(servicesData || []);
    setBookings(bookData || []);
    setNextLater((laterData || [])[0] || null);
    setUnseenNew(unseenData || []);
    setUnseenCancelled(cancelledData || []);

    const byClient = {};
    (unreadData || []).forEach(m => {
      if (!byClient[m.client_id]) byClient[m.client_id] = { clientId: m.client_id, count: 0, preview: m.content, latestAt: m.created_at };
      byClient[m.client_id].count += 1;
    });
    setUnreadByClient(Object.values(byClient).sort((a, b) => b.latestAt.localeCompare(a.latestAt)));

    // Nästa kund (idag efter nu, annars nästa dag)
    const nowHM = nowD.toTimeString().slice(0, 5);
    const next = (bookData || []).find(b => b.date > todayStr || (b.date === todayStr && hm(b.start_time) > nowHM)) || (laterData || [])[0] || null;
    if (next?.client_id) {
      const [{ data: tmpl }, { data: resp }, { data: note }, { count: visits }] = await Promise.all([
        supabase.from('anamnesis_templates').select('id').eq('therapist_id', therapist.id).maybeSingle(),
        supabase.from('anamnesis_responses').select('id, submitted_at').eq('therapist_id', therapist.id).eq('client_id', next.client_id).maybeSingle(),
        supabase.from('notes').select('content, created_at').eq('therapist_id', therapist.id).eq('client_id', next.client_id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('therapist_id', therapist.id).eq('client_id', next.client_id).neq('status', 'cancelled').lt('date', todayStr),
      ]);
      setNextInfo({
        anamnesis: !tmpl ? null : resp ? { filledAt: resp.submitted_at } : 'missing',
        note: note?.content || null,
        noteAt: note?.created_at || null,
        visits: typeof visits === 'number' ? visits + 1 : null,
      });
    } else {
      setNextInfo({ anamnesis: null, note: null, noteAt: null, visits: null });
    }

    // Aktivitet senaste 24 h: träningspass + besvarad anamnes
    if (clientIds.length > 0) {
      const [{ data: logs }, { data: anam }] = await Promise.all([
        supabase.from('exercise_logs').select('client_id, program_id, completed_at').in('client_id', clientIds).gte('completed_at', cutoff).order('completed_at', { ascending: false }),
        supabase.from('anamnesis_responses').select('client_id, submitted_at').eq('therapist_id', therapist.id).gte('submitted_at', cutoff),
      ]);
      const programIds = [...new Set((logs || []).map(l => l.program_id).filter(Boolean))];
      const programName = {};
      if (programIds.length > 0) {
        const { data: progs } = await supabase.from('programs').select('id, title').in('id', programIds);
        (progs || []).forEach(p => { programName[p.id] = p.title; });
      }
      const groups = {};
      (logs || []).forEach(l => {
        const day = l.completed_at?.split('T')[0];
        if (!day) return;
        const key = `${l.client_id}|${day}|${l.program_id}`;
        if (!groups[key]) groups[key] = { type: 'exercise', clientId: l.client_id, programName: programName[l.program_id] || null, latestAt: l.completed_at };
        else if (l.completed_at > groups[key].latestAt) groups[key].latestAt = l.completed_at;
      });
      const anamItems = (anam || []).map(a => ({ type: 'anamnesis', clientId: a.client_id, latestAt: a.submitted_at }));
      setActivity([...Object.values(groups), ...anamItems].sort((a, b) => b.latestAt.localeCompare(a.latestAt)).slice(0, 5));
    } else {
      setActivity([]);
    }

    setLoading(false);
  }, [therapist.id, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchHome(); }, [fetchHome]);
  useEffect(() => {
    const t = setInterval(fetchHome, 30000);
    return () => clearInterval(t);
  }, [fetchHome]);

  const markAllSeen = async () => {
    setMarkingAll(true);
    const [{ error: e1 }, { error: e2 }] = await Promise.all([
      supabase.from('bookings').update({ therapist_seen: true }).eq('therapist_id', therapist.id).eq('therapist_seen', false),
      supabase.from('messages').update({ read: true }).eq('therapist_id', therapist.id).eq('sender_role', 'client').eq('read', false),
    ]);
    setMarkingAll(false);
    if (e1 || e2) { showToast('Kunde inte markera allt som sett.', 'error'); return; }
    await fetchHome();
    if (onCountsChanged) onCountsChanged();
  };

  if (loading) return <p>Laddar...</p>;

  // ─── Beräkningar ─────────────────────────────────────────────────────────
  const todayStr = getDateStr(now);
  const nowHM = now.toTimeString().slice(0, 5);
  const nowMin = minutes(nowHM);
  const todayBookings = bookings.filter(b => b.date === todayStr);
  const nextBooking = bookings.find(b => b.date > todayStr || (b.date === todayStr && hm(b.start_time) > nowHM)) || nextLater;
  const isNextToday = nextBooking?.date === todayStr;
  const minutesUntilNext = isNextToday ? Math.max(0, minutes(nextBooking.start_time) - nowMin) : null;
  const nextWhen = !nextBooking ? null : isNextToday ? fmtUntil(minutesUntilNext) : nextBooking.date === getDateStr(addDays(now, 1)) ? 'imorgon' : fmtDateShort(nextBooking.date);

  const attHantera = unseenNew.length + unseenCancelled.length + unreadByClient.reduce((s, u) => s + u.count, 0);
  const weekAgo = addDays(now, -7);
  const newClientsWeek = clients.filter(c => c.created_at && new Date(c.created_at) > weekAgo).length;

  const firstName = therapist?.name?.split(' ')[0] || 'terapeut';
  const hour = now.getHours();
  const greeting = hour < 12 ? 'God morgon' : hour < 17 ? 'God eftermiddag' : 'God kväll';
  const dateLabel = `${['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'][now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]} · vecka ${isoWeek(now)}`;

  const summaryParts = [];
  summaryParts.push(todayBookings.length === 0 ? 'Inga bokningar idag' : `${todayBookings.length} ${todayBookings.length === 1 ? 'bokning' : 'bokningar'} idag`);
  if (nextBooking) summaryParts.push(<span key="n">nästa kund <span style={{ color: theme.accent_soft_bg, fontWeight: '600' }}>{isNextToday ? hm(nextBooking.start_time) : nextWhen}</span></span>);
  if (attHantera > 0) summaryParts.push(`${attHantera} ${attHantera === 1 ? 'sak' : 'saker'} att hantera`);

  // Idag-rader: bokningar + kommande lediga tider
  const activeDurations = services.filter(s => s.is_active).map(s => s.duration);
  const defaultDuration = activeDurations.length > 0 ? Math.min(...activeDurations) : 60;
  const todaySchedule = schedules.find(s => s.day_of_week === now.getDay());
  const todayBlocked = blockedTimes.filter(bt => bt.date === todayStr);
  const todayBlockedAll = todayBlocked.find(bt => bt.all_day);
  const overlapsBooking = (s, e) => todayBookings.some(b => s < hm(b.end_time) && e > hm(b.start_time));
  const overlapsBlock = (s, e) => todayBlocked.some(bt => bt.all_day || (s < hm(bt.end_time) && e > hm(bt.start_time)));
  const rows = todayBookings.map(b => ({ kind: 'booking', start: hm(b.start_time), end: hm(b.end_time), b }));
  if (todaySchedule && !todayBlockedAll) {
    generateSlots(todaySchedule, defaultDuration, todaySchedule.break_duration || 0)
      .filter(s => s.start >= nowHM && !overlapsBooking(s.start, s.end) && !overlapsBlock(s.start, s.end))
      .forEach(s => rows.push({ kind: 'free', start: s.start, end: s.end }));
  }
  rows.sort((a, b) => a.start.localeCompare(b.start));
  const lastEnd = rows.length > 0 ? rows[rows.length - 1].end : null;

  // Kommande 7 dagar
  const upcomingDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(now, i + 1);
    const ds = getDateStr(d);
    return {
      date: d, dateStr: ds,
      count: bookings.filter(b => b.date === ds).length,
      open: !!schedules.find(s => s.day_of_week === d.getDay()),
      blocked: blockedTimes.find(bt => bt.date === ds && bt.all_day) || null,
    };
  });
  const upcomingTotal = upcomingDays.reduce((s, d) => s + d.count, 0);

  // Att hantera – blandad lista sorterad på tid
  const inbox = [
    ...unseenNew.map(b => ({ type: 'new', at: b.created_at, b })),
    ...unseenCancelled.map(b => ({ type: 'cancelled', at: b.created_at, b })),
    ...unreadByClient.map(u => ({ type: 'message', at: u.latestAt, u })),
  ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  const accentText = onAccent(theme.accent);
  const btnPrimary = { background: theme.accent, color: accentText, border: `1px solid ${theme.accent}`, padding: '9px 14px', borderRadius: theme.btn_radius, cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const btnGhostDark = { background: 'rgba(255,255,255,0.10)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.22)', padding: '9px 14px', borderRadius: theme.btn_radius, cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit', whiteSpace: 'nowrap' };

  const stats = [
    { label: 'Bokningar idag', value: todayBookings.length, sub: todayBookings.length > 0 ? `Första ${hm(todayBookings[0].start_time)} · sista ${hm(todayBookings[todayBookings.length - 1].start_time)}` : 'Ledig dag' },
    { label: 'Nästa kund', value: nextBooking ? (isNextToday ? hm(nextBooking.start_time) : nextWhen) : '–', sub: nextBooking ? `${nextBooking.clients?.name || '–'}${isNextToday ? ` · ${nextWhen}` : ` · ${hm(nextBooking.start_time)}`}` : 'Inga kommande bokningar', accent: !!nextBooking },
    { label: 'Att hantera', value: attHantera, sub: attHantera === 0 ? 'Allt är hanterat' : [unseenNew.length > 0 && `${unseenNew.length} ${unseenNew.length === 1 ? 'ny bokning' : 'nya bokningar'}`, unseenCancelled.length > 0 && `${unseenCancelled.length} ${unseenCancelled.length === 1 ? 'avbokning' : 'avbokningar'}`, unreadByClient.length > 0 && `${unreadByClient.reduce((s, u) => s + u.count, 0)} meddelanden`].filter(Boolean).join(' · ') },
    { label: 'Aktiva kunder', value: clients.length, sub: newClientsWeek > 0 ? `${newClientsWeek} ${newClientsWeek === 1 ? 'ny' : 'nya'} denna vecka` : 'Inga nya denna vecka' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* ── Sidhuvud: mörkt band med accentglöd ── */}
      <div className="fr-home-hero" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: '30px 32px 72px', color: '#FFFFFF' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-140px', right: '-80px', width: '420px', height: '420px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.35, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div aria-hidden="true" style={{ position: 'absolute', bottom: '-180px', right: '260px', width: '320px', height: '320px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.18, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div aria-hidden="true" style={{ position: 'absolute', top: '24px', right: '36px', width: '160px', height: '160px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.14)', pointerEvents: 'none' }} />
        <div aria-hidden="true" style={{ position: 'absolute', top: '56px', right: '68px', width: '96px', height: '96px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.10)', pointerEvents: 'none' }} />
        <div className="fr-home-hero-inner" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.10em', textTransform: 'uppercase', color: theme.accent_soft_bg }}>{dateLabel}</div>
            <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', letterSpacing: '-0.015em', lineHeight: 1.1, color: '#FFFFFF' }}>{greeting}, {firstName}.</h1>
            <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.78)' }}>
              {summaryParts.map((p, i) => <React.Fragment key={i}>{i > 0 && ' · '}{p}</React.Fragment>)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <button type="button" onClick={onAddClient} style={btnGhostDark}>+ Ny kund</button>
            <button type="button" onClick={onNewBooking} style={{ ...btnPrimary, background: theme.hero_btn_bg, border: `1px solid ${theme.hero_btn_bg}`, color: theme.hero_btn_text }}>+ Ny bokning</button>
          </div>
        </div>
      </div>

      {/* ── Nyckeltal (överlappar bandet) ── */}
      <div className="fr-home-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', margin: '-60px 20px 0', position: 'relative' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
            <Label>{s.label}</Label>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: '800', lineHeight: 1, color: s.accent ? theme.accent_text : NEUTRAL.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Två kolumner ── */}
      <div className="fr-home-grid" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '16px', alignItems: 'start' }}>

        {/* Vänster */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>

          {/* Nästa kund */}
          <Card theme={theme}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 20px', borderBottom: `1px solid ${theme.card_border}`, background: theme.accent_muted }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Dot color={theme.accent} />
                <Label color={theme.accent_soft_text}>Nästa kund{nextWhen ? ` · ${nextWhen}` : ''}</Label>
              </div>
              {nextBooking && <span style={{ fontSize: '13px', color: NEUTRAL.text, fontVariantNumeric: 'tabular-nums' }}>{hm(nextBooking.start_time)}–{hm(nextBooking.end_time)}</span>}
            </div>
            {nextBooking ? (
              <>
                <div className="fr-home-next" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '999px', background: theme.accent_soft_bg, color: theme.accent_soft_text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: '700', flexShrink: 0 }}>
                    {initials(nextBooking.clients?.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: '700', color: NEUTRAL.ink }}>{nextBooking.clients?.name || '–'}</div>
                    <div style={{ fontSize: '13px', color: NEUTRAL.text }}>
                      {[
                        nextBooking.service_title_snapshot || nextBooking.service?.title || null,
                        `${minutes(nextBooking.end_time) - minutes(nextBooking.start_time)} min`,
                        nextBooking.price_snapshot != null ? `${nextBooking.price_snapshot} kr` : null,
                        nextInfo.visits != null ? `${nextInfo.visits}:${nextInfo.visits === 1 || nextInfo.visits === 2 ? 'a' : 'e'} besöket` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button type="button" onClick={() => { const c = clientById(nextBooking.client_id); if (c) onOpenClient(c); }} style={{ ...btnPrimary, padding: '8px 14px' }}>Öppna kundkort</button>
                </div>
                <div style={{ padding: '0 20px 16px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                  <div style={{ border: `1px solid ${theme.card_border}`, borderRadius: '10px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                    <Label>Anamnes</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: NEUTRAL.ink }}>
                      {nextInfo.anamnesis === null ? (
                        <span style={{ color: NEUTRAL.muted }}>Ingen anamnesmall</span>
                      ) : nextInfo.anamnesis === 'missing' ? (
                        <><Dot color={STATUS_COLORS.warning} size={7} />Ej besvarad</>
                      ) : (
                        <><Dot color={STATUS_COLORS.success} size={7} />Ifylld{nextInfo.anamnesis.filledAt ? ` ${new Date(nextInfo.anamnesis.filledAt).getDate()} ${MONTHS_SHORT[new Date(nextInfo.anamnesis.filledAt).getMonth()]}` : ''}</>
                      )}
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${theme.card_border}`, borderRadius: '10px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                    <Label>Senaste anteckning{nextInfo.noteAt ? ` · ${new Date(nextInfo.noteAt).getDate()} ${MONTHS_SHORT[new Date(nextInfo.noteAt).getMonth()]}` : ''}</Label>
                    <div style={{ fontSize: '13px', color: nextInfo.note ? NEUTRAL.ink : NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextInfo.note || 'Ingen anteckning ännu'}</div>
                  </div>
                </div>
              </>
            ) : (
              <EmptyNote>Inga kommande bokningar</EmptyNote>
            )}
          </Card>

          {/* Idag */}
          <Card theme={theme}>
            <CardHeader theme={theme} title="Idag" right={<LinkButton theme={theme} onClick={onOpenBookings}>Öppna kalendern →</LinkButton>} />
            {todayBlockedAll ? (
              <EmptyNote>Dagen är blockerad{todayBlockedAll.reason ? ` · ${todayBlockedAll.reason}` : ''}</EmptyNote>
            ) : rows.length === 0 ? (
              <EmptyNote>{todaySchedule ? 'Inga fler tider idag' : 'Inga tider idag'}</EmptyNote>
            ) : (
              <>
                {rows.map((r, i) => {
                  const borderBottom = `1px solid ${theme.card_border}`;
                  if (r.kind === 'free') {
                    return (
                      <div key={`f${r.start}`} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 20px', borderBottom, borderLeft: '3px solid transparent' }}>
                        <div style={{ width: '96px', fontSize: '13px', color: NEUTRAL.faint, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{r.start}–{r.end}</div>
                        <div style={{ flex: 1, border: `1px dashed ${NEUTRAL.line}`, borderRadius: '8px', padding: '7px 12px', fontSize: '12px', color: NEUTRAL.faint }}>Ledig</div>
                        <LinkButton theme={theme} onClick={onOpenBookings}>+ Boka</LinkButton>
                      </div>
                    );
                  }
                  const b = r.b;
                  const isDone = minutes(r.end) <= nowMin;
                  const isOngoing = minutes(r.start) <= nowMin && minutes(r.end) > nowMin;
                  const isNext = nextBooking?.id === b.id;
                  const c = clientById(b.client_id);
                  return (
                    <div
                      key={b.id}
                      onClick={() => c && onOpenClient(c)}
                      title={c ? 'Öppna kundkortet' : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 20px', borderBottom,
                        borderLeft: isOngoing ? `3px solid ${theme.accent}` : '3px solid transparent',
                        background: isOngoing ? theme.accent_muted : 'transparent',
                        opacity: isDone ? 0.6 : 1, cursor: c ? 'pointer' : 'default',
                      }}
                    >
                      <div style={{ width: '96px', fontSize: '13px', flexShrink: 0, fontVariantNumeric: 'tabular-nums', color: isOngoing ? theme.accent : isDone ? NEUTRAL.muted : NEUTRAL.ink, fontWeight: isOngoing || isNext ? '600' : '400' }}>{r.start}–{r.end}</div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.clients?.name || '–'}</div>
                        <div style={{ fontSize: '12px', color: NEUTRAL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[b.service_title_snapshot || b.service?.title || null, `${minutes(r.end) - minutes(r.start)} min`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      {isDone && <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Klar</span>}
                      {isOngoing && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '4px 10px', background: theme.accent, color: accentText, fontSize: '11px', fontWeight: '600' }}>
                          <Dot color={accentText} size={6} />Pågår nu
                        </span>
                      )}
                      {isNext && <span style={{ fontSize: '11px', fontWeight: '600', color: theme.accent_text, border: `1px solid ${theme.accent}`, borderRadius: '999px', padding: '3px 9px' }}>Nästa</span>}
                    </div>
                  );
                })}
                <div style={{ padding: '10px 20px', fontSize: '12px', color: NEUTRAL.faint }}>Inga fler tider efter {lastEnd}</div>
              </>
            )}
          </Card>
        </div>

        {/* Höger */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>

          {/* Att hantera */}
          <Card theme={theme}>
            <CardHeader
              theme={theme}
              title="Att hantera"
              right={attHantera > 0 && <LinkButton theme={theme} muted onClick={markAllSeen}>{markingAll ? 'Markerar…' : 'Markera alla som sedda'}</LinkButton>}
            >
              {attHantera > 0 && <span style={{ borderRadius: '999px', padding: '2px 8px', background: theme.accent, color: accentText, fontSize: '11px', fontWeight: '700' }}>{attHantera}</span>}
            </CardHeader>
            {inbox.length === 0 ? (
              <EmptyNote>Allt är hanterat</EmptyNote>
            ) : inbox.map((item, i) => {
              const last = i === inbox.length - 1;
              const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 20px', borderBottom: last ? 'none' : `1px solid ${theme.card_border}` };
              if (item.type === 'message') {
                const c = clientById(item.u.clientId);
                return (
                  <div key={`m${item.u.clientId}`} style={rowStyle}>
                    <Dot color={MSG_COLOR} style={{ marginTop: '6px' }} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ fontSize: '13px', color: NEUTRAL.ink }}><span style={{ fontWeight: '600' }}>{clientName(item.u.clientId)}</span> skrev{item.u.count > 1 ? ` (${item.u.count} nya)` : ''}</div>
                      <div style={{ fontSize: '12px', color: NEUTRAL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>”{item.u.preview}”</div>
                      <div style={{ fontSize: '11px', color: NEUTRAL.faint }}>{fmtRelative(item.u.latestAt, now)}</div>
                    </div>
                    {c && <LinkButton theme={theme} onClick={() => onOpenClient(c)}>Svara</LinkButton>}
                  </div>
                );
              }
              const b = item.b;
              const cancelled = item.type === 'cancelled';
              return (
                <div key={b.id} style={rowStyle}>
                  <Dot color={cancelled ? STATUS_COLORS.error : theme.accent} style={{ marginTop: '6px' }} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ fontSize: '13px', color: NEUTRAL.ink }}><span style={{ fontWeight: '600' }}>{b.clients?.name || '–'}</span> {cancelled ? 'avbokade' : 'bokade en tid'}</div>
                    <div style={{ fontSize: '12px', color: NEUTRAL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {fmtDateShort(b.date)} {hm(b.start_time)}{cancelled ? ' · tiden är ledig igen' : (b.service_title_snapshot || b.service?.title) ? ` · ${b.service_title_snapshot || b.service?.title}` : ''}
                    </div>
                    {!cancelled && <div style={{ fontSize: '11px', color: NEUTRAL.faint }}>{fmtRelative(b.created_at, now)}</div>}
                  </div>
                  <LinkButton theme={theme} onClick={onOpenBookings}>Visa</LinkButton>
                </div>
              );
            })}
          </Card>

          {/* Kommande 7 dagar */}
          <Card theme={theme}>
            <CardHeader theme={theme} title="Kommande 7 dagar" right={<span style={{ fontSize: '12px', color: NEUTRAL.muted }}>{upcomingTotal} {upcomingTotal === 1 ? 'bokning' : 'bokningar'}</span>} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px', padding: '14px 16px' }}>
              {upcomingDays.map(d => {
                const closed = !d.open;
                const bg = d.blocked
                  ? `repeating-linear-gradient(135deg, ${theme.card_bg} 0 6px, ${NEUTRAL.page} 6px 12px)`
                  : closed ? NEUTRAL.page : d.count > 0 ? theme.accent_muted : 'transparent';
                return (
                  <div
                    key={d.dateStr}
                    onClick={onOpenBookings}
                    title="Öppna kalendern"
                    style={{ borderRadius: '10px', padding: '10px 4px', textAlign: 'center', background: bg, border: !d.blocked && !closed && d.count === 0 ? `1px dashed ${NEUTRAL.line}` : '1px solid transparent', display: 'flex', flexDirection: 'column', gap: '4px', cursor: 'pointer', minWidth: 0 }}
                  >
                    <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: closed ? NEUTRAL.faint : NEUTRAL.muted }}>{DAY_NAMES_SHORT[d.date.getDay()]}</div>
                    {d.blocked ? (
                      <div style={{ fontSize: '11px', fontWeight: '600', color: NEUTRAL.text, lineHeight: '22px' }}>Block.</div>
                    ) : closed ? (
                      <div style={{ fontSize: '11px', fontWeight: '500', color: NEUTRAL.faint, lineHeight: '22px' }}>Stängt</div>
                    ) : (
                      <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: '800', color: d.count > 0 ? theme.accent : NEUTRAL.faint, lineHeight: '22px' }}>{d.count}</div>
                    )}
                    <div style={{ fontSize: '10px', color: closed ? NEUTRAL.faint : NEUTRAL.muted }}>{d.date.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Aktivitet */}
          <Card theme={theme}>
            <CardHeader theme={theme} title="Aktivitet" right={<span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Senaste 24 h</span>} />
            {activity.length === 0 ? (
              <EmptyNote>Ingen aktivitet de senaste 24 h</EmptyNote>
            ) : activity.map((a, i) => {
              const c = clientById(a.clientId);
              return (
                <div
                  key={`${a.clientId}-${a.latestAt}`}
                  onClick={() => c && onOpenClient(c)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 20px', borderBottom: i < activity.length - 1 ? `1px solid ${theme.card_border}` : 'none', cursor: c ? 'pointer' : 'default' }}
                >
                  <Dot color={STATUS_COLORS.success} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: '600' }}>{clientName(a.clientId)}</span>
                    <span style={{ color: NEUTRAL.text }}>{a.type === 'anamnesis' ? ' · anamnes besvarad' : a.programName ? ` · pass loggat · ${a.programName}` : ' · pass loggat'}</span>
                  </div>
                  <span style={{ fontSize: '11px', color: NEUTRAL.faint, whiteSpace: 'nowrap' }}>{fmtRelative(a.latestAt, now)}</span>
                </div>
              );
            })}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default HomeView;
